import React, { useState } from "react";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { X, Paperclip, Calendar, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";
import useSession from "@/v2/hooks/supabase/useSession";
import { useProfileTracking } from "@/v2/hooks/profiles/useProfileTracking";
import { ProfileData } from "@/v2/hooks/profiles/useProfiles";
interface HireMeModalProps {
  profile: ProfileData;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  designerName: string;
  designerId: string;
  designerUsername: string;
  onSubmitSuccess: () => void;
}

export interface HireMeFormData {
  projectDescription: string;
  budget: string;
  currency: string;
  deadline: string;
  email: string;
  referenceFiles: File[] | null;
  name: string;
}

const HireMeModal: React.FC<HireMeModalProps> = ({
  isOpen,
  onOpenChange,
  designerName,
  designerId,
  designerUsername,
  onSubmitSuccess,
  profile,
}) => {
  const { toast } = useToast();
  const { user } = useSession();
  const { handleHireMeRequest, loading } = useProfileTracking();
  const [formData, setFormData] = useState<HireMeFormData>({
    projectDescription: "",
    budget: "",
    currency: "USD",
    deadline: "",
    email: "",
    referenceFiles: null,
    name: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<
    Partial<Record<keyof HireMeFormData, string>>
  >({});

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof HireMeFormData, string>> = {};

    if (!formData.projectDescription.trim()) {
      newErrors.projectDescription = "Project description is required";
    }

    if (!formData.budget.trim()) {
      newErrors.budget = "Budget is required";
    } else if (isNaN(Number(formData.budget)) || Number(formData.budget) <= 0) {
      newErrors.budget = "Please enter a valid budget amount";
    } else if (Number(formData.budget) < 100) {
      newErrors.budget = "Budget must be at least $100";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.deadline.trim()) {
      newErrors.deadline = "Deadline is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      // Validate file types and sizes
      const validFiles = files.filter((file) => {
        const isValidType = [
          "application/pdf",
          "image/jpeg",
          "image/png",
        ].includes(file.type);
        const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB limit
        return isValidType && isValidSize;
      });

      if (validFiles.length !== files.length) {
        toast({
          title: "Invalid files",
          description:
            "Some files were rejected. Only PDF, JPG, and PNG files up to 5MB are allowed.",
          variant: "destructive",
        });
      }

      setFormData((prev) => ({ ...prev, referenceFiles: validFiles }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload files if any
      let referenceFilesUrls: string[] = [];
      if (formData.referenceFiles && formData.referenceFiles.length > 0) {
        const uploadPromises = formData.referenceFiles.map(async (file) => {
          const fileExt = file.name.split(".").pop();
          const fileName = `${Math.random()}.${fileExt}`;
          const filePath = `hire-requests/${designerId}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from("reference-files")
            .upload(filePath, file);

          if (uploadError) throw uploadError;

          const {
            data: { publicUrl },
          } = supabase.storage.from("reference-files").getPublicUrl(filePath);

          return publicUrl;
        });

        referenceFilesUrls = await Promise.all(uploadPromises);
      }

      const response = await handleHireMeRequest(profile, formData);
      if (response) {
        toast({
          title: "Request sent successfully",
          description: `${designerName} will be notified of your request.`,
        });
      }

      // Insert hire request
      // const { error: insertError } = await supabase
      //   .from("hire_requests")
      //   .insert({
      //     profile_id: designerId,
      //     profile_username: designerUsername,
      //     requester_id: user?.id || null,
      //     requester_name: formData.name,
      //     requester_email: formData.email,
      //     project_description: formData.projectDescription,
      //     budget: parseFloat(formData.budget),
      //     currency: formData.currency,
      //     deadline: formData.deadline || null,
      //     reference_files:
      //       referenceFilesUrls.length > 0 ? referenceFilesUrls : null,
      //     status: "pending",
      //   });

      // if (insertError) throw insertError;

      // toast({
      //   title: "Request sent successfully",
      //   description: `${designerName} will be notified of your request.`,
      // });

      onOpenChange(false);
      onSubmitSuccess();
    } catch (error) {
      console.error("Error submitting hire request:", error);
      toast({
        title: "Error",
        description: "Failed to send request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    field: keyof HireMeFormData,
    value: string | File[] | null
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="h-full flex flex-col justify-center items-center max-w-none bg-white/20 backdrop-blur-md border-none shadow-none">
        <div className="flex justify-center w-full mb-4">
          <DialogClose className="flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-md hover:bg-gray-50 transition-colors">
            <X size={16} className="text-gray-500" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </div>
        <div className="relative w-full max-w-[500px] bg-white rounded-xl overflow-hidden flex flex-col px-6 md:px-12 max-h-[90vh] overflow-y-auto py-6">
          <div className="flex w-full pb-2">
            <h2 className="text-xl font-semibold">Hire {designerName}</h2>
          </div>

          <div className="flex-1 p-0 md:py-4">
            <div className="space-y-6">
              <div className="space-y-2">
                <label
                  htmlFor="name"
                  className="text-sm font-medium flex items-center"
                >
                  Your Name <span className="text-red-500 ml-1">*</span>
                </label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Your full name"
                  className={`w-full rounded-md border ${
                    errors.name ? "border-red-500" : "border-input"
                  } bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
                />
                {errors.name && (
                  <p className="text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="text-sm font-medium flex items-center"
                >
                  Your Email <span className="text-red-500 ml-1">*</span>
                </label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="your@email.com"
                  className={`w-full rounded-md border ${
                    errors.email ? "border-red-500" : "border-input"
                  } bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
                />
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email}</p>
                )}
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="projectDescription"
                  className="text-sm font-medium flex items-center"
                >
                  Project Description{" "}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <Textarea
                  id="projectDescription"
                  value={formData.projectDescription}
                  onChange={(e) =>
                    handleInputChange("projectDescription", e.target.value)
                  }
                  placeholder="Describe your project requirements..."
                  className={`min-h-[80px] rounded-md border ${
                    errors.projectDescription
                      ? "border-red-500"
                      : "border-input"
                  } bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
                />
                {errors.projectDescription && (
                  <p className="text-sm text-red-500">
                    {errors.projectDescription}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="budget"
                  className="text-sm font-medium flex items-center"
                >
                  Your Budget <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <Input
                    id="budget"
                    type="number"
                    value={formData.budget}
                    onChange={(e) =>
                      handleInputChange("budget", e.target.value)
                    }
                    placeholder="USD 0.0"
                    className={`flex-1 rounded-md border ${
                      errors.budget ? "border-red-500" : "border-input"
                    } focus-visible:ring-0 focus-visible:ring-offset-0`}
                  />
                </div>
                {errors.budget && (
                  <p className="text-sm text-red-500">{errors.budget}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="deadline" className="text-sm font-medium">
                  Deadline
                </label>
                <div className="relative flex items-center">
                  <Input
                    id="deadline"
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    value={formData.deadline}
                    onChange={(e) =>
                      handleInputChange("deadline", e.target.value)
                    }
                    className={`w-full pr-8 rounded-md border ${
                      errors.deadline ? "border-red-500" : "border-input"
                    } bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
                  />
                  <Calendar
                    size={16}
                    className="absolute right-3 text-gray-400 pointer-events-none"
                  />
                </div>
                {errors.deadline && (
                  <p className="text-sm text-red-500">{errors.deadline}</p>
                )}
              </div>

              {/* <div className="space-y-2">
                <label htmlFor="referenceFiles" className="text-sm font-medium">
                  Attach Reference Files
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="flex items-center text-blue-600 hover:underline focus:outline-none text-sm font-medium"
                    onClick={() => document.getElementById('referenceFiles')?.click()}
                  >
                    <Paperclip size={16} className="mr-1" />
                    Click to attach files
                  </button>
                  <span className="text-xs text-gray-400 ml-2">PDF, JPG, PNG (max 5MB)</span>
                </div>
                {formData.referenceFiles?.length ? (
                  <p className="text-xs text-gray-600 mt-1">{formData.referenceFiles.length} file(s) selected</p>
                ) : null}
                <Input
                  id="referenceFiles"
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="sr-only"
                  accept=".pdf,.jpg,.jpeg,.png"
                />
              </div> */}

              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || loading.hireMe}
                className="text-sm px-8 py-3 w-full bg-black text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting || loading.hireMe ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending Request...
                  </>
                ) : (
                  "Submit Request"
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HireMeModal;
